import { QuestionHistoryFile, QuestionHistoryEntry } from "./questionsHistory.types";
import { SITE_SERVER_URL } from "@/shared/config/player";

const PERSONAL_DATA_WORKER_URL =
    "https://personal-data-worker.torsunovoleggen.workers.dev";

const HISTORY_PATH = "questions/history.json";
const MAX_ENTRIES = 500;

export class SessionExpiredError extends Error {
    constructor() {
        super("Session expired");
        this.name = "SessionExpiredError";
    }
}

export async function fetchQuestionHistory(
    token: string,
): Promise<QuestionHistoryFile | null> {
    try {
        const response = await fetch(
            `${PERSONAL_DATA_WORKER_URL}/data/${HISTORY_PATH}`,
            {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            },
        );

        if (response.status === 401) throw new SessionExpiredError();
        if (!response.ok) return null;

        const data = await response.json();
        return data as QuestionHistoryFile;
    } catch (e) {
        if (e instanceof SessionExpiredError) throw e;
        return null;
    }
}

export async function saveQuestionHistory(
    token: string,
    data: QuestionHistoryFile,
): Promise<void> {
    if (data.entries.length > MAX_ENTRIES) {
        const overflow = data.entries.slice(MAX_ENTRIES);
        data = { ...data, entries: data.entries.slice(0, MAX_ENTRIES) };
        await archiveOldEntries(token, overflow);
    }

    await fetch(`${PERSONAL_DATA_WORKER_URL}/data/${HISTORY_PATH}`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });
}

async function archiveOldEntries(
    token: string,
    entries: QuestionHistoryEntry[],
): Promise<void> {
    const timestamp = Date.now();
    const archivePath = `questions/history_archive_${timestamp}.json`;

    const archiveData: QuestionHistoryFile = { version: 1, entries };

    await fetch(`${PERSONAL_DATA_WORKER_URL}/data/${archivePath}`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(archiveData),
    });
}

export async function validateHistoryHashes(
    hashes: string[],
): Promise<string[]> {
    if (hashes.length === 0) return [];
    const response = await fetch(
        `${SITE_SERVER_URL}/api/question-database/validate-hashes`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ hashes }),
        },
    );
    if (!response.ok) throw new Error("Failed to validate hashes");
    const data = await response.json();
    return data.valid_hashes || [];
}
